import { Title } from 'components/Title';
import { FC, useEffect, useMemo, useState } from 'react';
import styles from './CreateContractForm.module.scss';
import { Label } from 'components/CreateOrderForm';
import { Box, Text, type SelectProps, Group, Select, Loader } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import ContentLoader from 'react-content-loader';
import moment from 'moment';
import { ReactComponent as CalendarSvg } from 'assets/img/filter/iconCalendarBig.svg';
import { ReactComponent as IconAlert } from './img/iconAlertOutline.svg';
import { ReactComponent as IconChevron } from './img/iconChewron.svg';
import { ReactComponent as IconCloseGray } from './img/iconCloseGray.svg';
import { ReactComponent as IconTouchId } from './img/iconTouchId.svg';
import { Button } from 'ui/Button';
import { useGetWorkersQuery } from 'redux/Api/ordersApi/ordersApi';
import { ReactComponent as IconSearchEnter } from 'ui/InputSearch/img/enter.svg';
import { InfoEmployeds } from 'components/InfoEmployeds';
import { getFormatPhone } from 'utils/getFotmatPhone';
import {
  useCreateContractMutation,
  useGetOfertaTypeContractsQuery,
  useGetTypeContractQuery,
} from 'redux/Api/contractsApi/contractsApi';
import classNames from 'classnames';
import { Avatar } from 'components/Avatar';
import {
  ApiError,
  AvatarStatus,
  CreateContractFormTypes,
  WorkerAppInfo,
  WorkerData,
} from './types';
import useYandexMetrica from 'hooks/useYandexMetrica';
import { useGetProfileQuery } from 'redux/Api/profileApi/ProfileApi';

interface OfertaType {
  id: number;
  title: string;
}

export const CreateContractForm: FC<CreateContractFormTypes> = ({
  onComplete,
  mobile = false,
  defaultWorker = null,
  workerSurname = null,
}) => {
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | null>(defaultWorker);
  const { data: profileData } = useGetProfileQuery();
  const [selectedContractKind, setSelectedContractKind] = useState<string | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endDatePreset, setEndDatePreset] = useState<string | null>('3 мес');
  const [error, setError] = useState<string | null>(null);
  const [workerError, setWorkerError] = useState<string | null>(null);
  const [contractKindError, setContractKindError] = useState<string | null>(null);
  const [contractIdError, setContractIdError] = useState<string | null>(null);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const { data: typeContract, isLoading: isTypeContractLoading } =
    useGetTypeContractQuery();
  const [workerSearchValue, setWorkerSearchValue] = useState('');
  const [workerSearchApplied, setWorkerSearchApplied] = useState<
    { search?: string } | undefined
  >(undefined);
  const showContract = Boolean(typeContract && typeContract.length > 0);
  const showWorkerSelect = !showContract || Boolean(selectedContractKind);
  const showOfertaSelect = selectedContractKind === 'oferta_accept';
  const [ErrorTextCreateContractTetrika, setErrorTextCreateContractTetrika] = useState<
    string | null
  >(null);
  const [isLoadingWorker, setIsLoadingWorker] = useState(false);

  const { data, isFetching } = useGetWorkersQuery(
    {
      forContract: true,
      search: workerSurname ? workerSurname : workerSearchApplied?.search,
      ...(showContract && selectedContractKind
        ? { unified_contract_type: selectedContractKind }
        : {}),
    },
    {
      skip: isTypeContractLoading || !showWorkerSelect,
    },
  );
  const { data: dataTypeContract, isLoading } = useGetOfertaTypeContractsQuery(
    undefined,
    {
      skip: !showOfertaSelect,
    },
  );
  const { sendGoal } = useYandexMetrica();
  const [createContract, { isLoading: isCreating }] = useCreateContractMutation();

  const isWorkerArray = (workers: unknown): workers is WorkerData[] =>
    Array.isArray(workers) &&
    workers.every(
      (worker) =>
        typeof worker === 'object' &&
        worker !== null &&
        'id' in worker &&
        typeof (worker as { id: unknown }).id === 'number' &&
        'name' in worker &&
        typeof (worker as { name: unknown }).name === 'string' &&
        'surname' in worker &&
        typeof (worker as { surname: unknown }).surname === 'string' &&
        'phone' in worker &&
        typeof (worker as { phone: unknown }).phone === 'string',
    );

  const workers = isWorkerArray(data?.data) ? data.data : [];

  const getWorkerStatus = (status: WorkerAppInfo['app_status']): number =>
    typeof status === 'number' ? status : 0;

  const getAvatarStatus = (status: WorkerAppInfo['app_status']): AvatarStatus =>
    typeof status === 'string' ? (status as AvatarStatus) : '';

  const selectWorker = workers.map((item) => ({
    value: item.id.toString(),
    label: `${item.surname} ${item.name} ${item.patronymic || ''} ${item.phone}`,
  }));

  const selectContractKind = typeContract?.map((item) => ({
    value: item.type,
    label: item.title,
  }));

  const showDateBlock =
    !showContract || Boolean(selectedContractKind && !showOfertaSelect);

  const selectContractType = (dataTypeContract || [])
    .filter((item: OfertaType) => {
      return item?.title;
    })
    .map((item: OfertaType) => ({
      value: item.id.toString(),
      label: item.title,
    }));

  const renderSelectOption: SelectProps['renderOption'] = ({ option }) => {
    const worker = workers.find((workerItem) => workerItem.id === Number(option.value));

    if (!worker) {
      return null;
    }

    return (
      <Group flex='1' gap='xs' h='44px' onClick={() => setSelectedWorkerId(worker.id)}>
        <InfoEmployeds
          status={getWorkerStatus(worker.worker_app?.app_status)}
          avatar={worker.worker_app?.avatar ?? null}
          id={worker.id}
          name={worker.name}
          surname={worker.surname}
          patronymic={worker.patronymic}
          phone={getFormatPhone(worker.phone)}
          withPhoneIcon={false}
          nationality={worker.worker_app?.nationality ?? ''}
          compact
        />
      </Group>
    );
  };

  const selectedWorker = workers.find((worker) => worker.id === selectedWorkerId);

  useEffect(() => {
    if (!selectedWorkerId || !selectedContractKind || isFetching) {
      if (!selectedWorkerId || !selectedContractKind) {
        setErrorTextCreateContractTetrika(null);
      }
      return;
    }

    const contractKindLabel = selectContractKind
      ?.find((item) => item.value === selectedContractKind)
      ?.label?.toLowerCase();
    const signedWord =
      selectedContractKind === 'oferta_accept' ? 'подписано' : 'подписан';

    setErrorTextCreateContractTetrika(
      selectedWorker
        ? null
        : `С данным исполнителем уже ${signedWord} ${contractKindLabel}`,
    );
    setIsLoadingWorker(false);
  }, [selectedWorkerId, selectedWorker, selectedContractKind, isFetching]);

  const handleSubmit = async () => {
    setError(null);
    setWorkerError(null);
    setStartDateError(null);
    setEndDateError(null);
    setContractKindError(null);
    setContractIdError(null);

    let hasError = false;

    if (!selectedWorkerId) {
      setWorkerError('Выбери исполнителя');
      hasError = true;
    } else if (
      selectedWorker?.worker_app?.nationality === 'Россия' &&
      !profileData?.settings?.tetrika
    ) {
      setWorkerError('Договор можно создать только с нерезидентом');
      hasError = true;
    }

    if (!selectedContractKind && showContract) {
      setContractKindError('Выбери тип договора');
      hasError = true;
    }

    if (!selectedContractId && showContract && showOfertaSelect) {
      setContractIdError('Выбери договор');
      hasError = true;
    }

    if (showDateBlock) {
      if (!startDate) {
        setStartDateError('Укажи дату начала');
        hasError = true;
      }

      if (!endDate) {
        setEndDateError('Укажи дату окончания');
        hasError = true;
      }

      if (startDate && moment(startDate).isBefore(moment().startOf('day'))) {
        setStartDateError('Дата начала договора не может быть раньше текущей даты');
        hasError = true;
      }

      if (endDate && moment(endDate).isBefore(moment().startOf('day'))) {
        setEndDateError('Дата окончания договора не может быть раньше текущей даты');
        hasError = true;
      }
    }

    if (hasError) {
      return;
    }

    if (selectedWorkerId === null) {
      return;
    }

    if (showDateBlock && startDate === null) {
      return;
    }

    try {
      const body: {
        worker_id: number;
        date_start?: string;
        date_end?: string | null;
        oferta_id?: number | null;
        type?: string | null;
      } = {
        worker_id: selectedWorkerId,
      };

      if (showContract && selectedContractKind) {
        body.type = selectedContractKind;
      }

      if (showOfertaSelect) {
        body.oferta_id = selectedContractId ? Number(selectedContractId) : null;
      }

      if (showDateBlock && startDate) {
        body.date_start = moment(startDate).format('YYYY-MM-DD');
        body.date_end = endDate ? moment(endDate).format('YYYY-MM-DD') : null;
      }

      const result = await createContract(body).unwrap();

      onComplete?.({ id: result.id, number: String(result.number) });
    } catch (err: unknown) {
      const errorMessage =
        (typeof err === 'object' && err !== null
          ? (err as ApiError).data?.message ?? (err as ApiError).data?.error?.message
          : undefined) || 'Ошибка при создании договора';
      setError(errorMessage);
    }
  };

  const start_date = startDate || new Date();

  const showBannerResident = useMemo(() => {
    if (!selectedWorker) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = startDate ? new Date(startDate) : today;

    return selectedWorker?.worker_app?.nationality !== 'Россия' && start < today;
  }, [startDate, selectedWorker?.worker_app?.nationality]);

  useEffect(() => {
    setEndDate(moment(start_date).add('3', 'month').toDate());
  }, []);

  const showBannerDate = useMemo(() => {
    if (!startDate || !endDate) return false;

    const limitDate = moment(startDate).add(3, 'months');

    return moment(endDate).isAfter(limitDate);
  }, [startDate, endDate]);

  const endDatePresets: Record<
    string,
    { amount: number; unit: moment.unitOfTime.DurationConstructor }
  > = {
    Месяц: { amount: 1, unit: 'month' },
    '3 мес': { amount: 3, unit: 'month' },
    '6 мес': { amount: 6, unit: 'month' },
    Год: { amount: 1, unit: 'year' },
  };

  const getPresetForRange = (start: Date | null, end: Date | null): string | null => {
    if (!start || !end) return null;

    const startMoment = moment(start).startOf('day');
    const endMoment = moment(end).startOf('day');

    const matchedPreset = Object.entries(endDatePresets).find(([, { amount, unit }]) =>
      endMoment.isSame(startMoment.clone().add(amount, unit), 'day'),
    );

    return matchedPreset ? matchedPreset[0] : null;
  };

  const handleSelectPreset = (preset: string) => {
    setEndDatePreset(preset);

    const presetConfig = endDatePresets[preset];
    if (!presetConfig) return;

    setEndDate(moment(start_date).add(presetConfig.amount, presetConfig.unit).toDate());
  };

  useEffect(() => {
    setEndDatePreset(getPresetForRange(startDate, endDate));
  }, [startDate, endDate]);

  return isTypeContractLoading ? (
    <ContentLoader
      speed={2}
      height={'500px'}
      width={'575px'}
      className={styles.tab_loader}
      backgroundColor={'#FFFFFF'}
      foregroundColor={'#D8E4FB8C'}>
      <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
    </ContentLoader>
  ) : (
    <div className={classNames(styles.root, mobile && styles.mobile)}>
      <div>
        {mobile ? (
          <Text fz='20px' fw='600' lh='24px'>
            Создать договор
          </Text>
        ) : (
          <Title title='Создать договор' />
        )}
      </div>

      {showContract && (
        <div>
          <div className={styles.labelWithBtn}>
            <Label text='Тип договора' />
          </div>

          <Select
            data={selectContractKind}
            nothingFoundMessage='Ничего не найдено'
            placeholder='Выбери тип договора'
            styles={{
              input: {
                height: 48,
                fontSize: '16px',
                border:
                  contractKindError || ErrorTextCreateContractTetrika
                    ? '1px solid red'
                    : 'none',
                padding: '0 16px',
                cursor: 'pointer',
              },
              option: {
                minHeight: '48px',
                height: 'auto',
                fontSize: '16px',
                padding: '12px 16px',
                lineHeight: '1.4',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
              },
              dropdown: {
                boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
                border: 'none',
                borderRadius: 4,
                padding: 0,
              },
              section: {
                marginRight: '6px',
              },
            }}
            comboboxProps={{
              position: 'bottom',
              offset: 0,
              zIndex: 100001,
            }}
            value={selectedContractKind}
            onChange={(val) => {
              if (!val) return;
              setSelectedContractKind(val);
              defaultWorker ? setIsLoadingWorker(true) : setIsLoadingWorker(false);
              setSelectedContractId(null);
              setContractKindError(null);
              setErrorTextCreateContractTetrika(null);
              setContractIdError(null);
              if (!defaultWorker) {
                setSelectedWorkerId(null);
                setWorkerSearchValue('');
                setWorkerSearchApplied(undefined);
              }
            }}
            withCheckIcon={false}
            rightSection={
              isFetching ? <Loader size='xs' color='#002cfb' /> : <IconChevron />
            }
            allowDeselect={false}
          />
          {contractKindError && <p className={styles.field_error}>{contractKindError}</p>}
          {ErrorTextCreateContractTetrika && (
            <p className={styles.field_error}>{ErrorTextCreateContractTetrika}</p>
          )}
        </div>
      )}

      {!ErrorTextCreateContractTetrika && !isLoadingWorker && showWorkerSelect && (
        <div>
          <div className={styles.labelWithBtn}>
            <Label text='Исполнитель' />
          </div>
          {isFetching && !workerSearchApplied && !selectedWorkerId ? (
            <ContentLoader
              speed={2}
              height={'48px'}
              width={'100%'}
              className={styles.tab_loader}
              backgroundColor={'#FFFFFF'}
              foregroundColor={'#D8E4FB8C'}>
              <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
            </ContentLoader>
          ) : (
            <>
              {selectedWorkerId ? (
                <div className={styles.selectedWorker}>
                  <div className={styles.selectedWorker_left}>
                    <Avatar
                      height={'36px'}
                      width={'36px'}
                      avatar={selectedWorker?.worker_app?.avatar ?? null}
                      statusActive={getAvatarStatus(
                        selectedWorker?.worker_app?.app_status,
                      )}
                      id={selectedWorker?.id}
                      name={selectedWorker?.name}
                      surname={selectedWorker?.surname}
                      isDeleted={selectedWorker?.is_deleted}
                    />
                    <div className={styles.selectedWorker_info}>
                      <span className={styles.selectedWorker_name}>
                        {selectedWorker?.surname} {selectedWorker?.name}{' '}
                        {selectedWorker?.patronymic}
                      </span>
                      <div className={styles.selectedWorker_subinfo}>
                        <span>{getFormatPhone(selectedWorker?.phone)}</span>
                        <span>{selectedWorker?.worker_app?.nationality}</span>
                      </div>
                    </div>
                  </div>
                  {!defaultWorker && (
                    <IconCloseGray
                      onClick={() => {
                        setSelectedWorkerId(null), setWorkerSearchValue('');
                      }}
                      className={styles.selectedWorker_close}
                    />
                  )}
                </div>
              ) : (
                <div
                  onKeyDownCapture={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' && workerSearchValue.trim()) {
                      e.preventDefault();
                      e.stopPropagation();
                      setWorkerSearchApplied({ search: workerSearchValue.trim() });
                    }
                  }}>
                  <Select
                    placeholder='Выбери исполнителя'
                    data={selectWorker || []}
                    nothingFoundMessage='Ничего не найдено'
                    renderOption={renderSelectOption}
                    rightSection={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {workerSearchValue.trim() && (
                          <Box
                            component='button'
                            type='button'
                            onPointerDown={(e: React.PointerEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setWorkerSearchApplied(
                                workerSearchValue.trim()
                                  ? { search: workerSearchValue.trim() }
                                  : undefined,
                              );
                            }}
                            onTouchStart={(e: React.TouchEvent) => {
                              e.stopPropagation();
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              padding: 0,
                              border: 'none',
                              background: 'none',
                            }}>
                            {isFetching ? (
                              <div style={{ paddingRight: '6px' }}>
                                <Loader size='xs' color='#002cfb' />
                              </div>
                            ) : (
                              <IconSearchEnter style={{ width: 70, height: 18 }} />
                            )}
                          </Box>
                        )}
                        <IconChevron />
                      </div>
                    }
                    rightSectionPointerEvents='auto'
                    searchable
                    searchValue={workerSearchValue}
                    onSearchChange={(value) => {
                      setWorkerSearchValue(value);
                      if (!value.trim()) setWorkerSearchApplied(undefined);
                    }}
                    filter={({ options }) => options}
                    withErrorStyles={false}
                    styles={{
                      input: {
                        height: 48,
                        fontSize: '16px',
                        border: workerError ? '1px solid red' : 'none',
                        padding: '0 16px',
                      },
                      dropdown: {
                        boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
                        border: 'none',
                        borderRadius: 4,
                        padding: 0,
                      },
                      section: {
                        marginRight: '12px',
                        width: '100px',
                        display: 'flex',
                        justifyContent: 'end',
                      },
                    }}
                    comboboxProps={{
                      position: 'bottom',
                      offset: 0,
                      zIndex: 100001,
                    }}
                    allowDeselect={false}
                  />
                </div>
              )}
            </>
          )}
          {workerError && <p className={styles.field_error}>{workerError}</p>}
        </div>
      )}

      {!ErrorTextCreateContractTetrika &&
        !isLoadingWorker &&
        showContract &&
        showOfertaSelect && (
          <div>
            <div className={styles.labelWithBtn}>
              <Label text='Договор' />
            </div>

            {isLoading ? (
              <ContentLoader
                speed={2}
                height={'48px'}
                width={'100%'}
                className={styles.tab_loader}
                backgroundColor={'#FFFFFF'}
                foregroundColor={'#D8E4FB8C'}>
                <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
              </ContentLoader>
            ) : (
              <Select
                data={selectContractType}
                nothingFoundMessage='Ничего не найдено'
                placeholder='Выбери договор'
                styles={{
                  input: {
                    height: 48,
                    fontSize: '16px',
                    border: contractIdError ? '1px solid red' : 'none',
                    padding: '0 16px',
                    cursor: 'pointer',
                  },
                  option: {
                    minHeight: '48px',
                    height: 'auto',
                    fontSize: '16px',
                    padding: '12px 16px',
                    lineHeight: '1.4',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  },
                  dropdown: {
                    boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
                    border: 'none',
                    borderRadius: 4,
                    padding: 0,
                  },
                  section: {
                    marginRight: '6px',
                  },
                }}
                comboboxProps={{
                  position: 'bottom',
                  offset: 0,
                  zIndex: 100001,
                }}
                value={selectedContractId}
                onChange={(val) => {
                  if (!val) return;
                  setSelectedContractId(val);
                  setContractIdError(null);
                }}
                withCheckIcon={false}
                rightSection={<IconChevron />}
                allowDeselect={false}
              />
            )}
            {contractIdError && <p className={styles.field_error}>{contractIdError}</p>}
          </div>
        )}

      {!ErrorTextCreateContractTetrika && !isLoadingWorker && showDateBlock && (
        <>
          {showBannerResident ? (
            <div className={styles.info}>
              <div className={styles.info_top}>
                <div className={styles.info_title}>
                  <IconAlert />
                  <span>Рекомендуемая дата начала - сегодня</span>
                </div>
              </div>
              <div>
                <p>
                  При установлении даты договора ранее текущей, возможно, будет нарушен
                  срок уведомления МВД по договорам с нерезидентами РФ
                </p>{' '}
              </div>
            </div>
          ) : null}

          <div>
            <Label text='Дата начала' />
            <DateInput
              value={startDate}
              onChange={(newStartDate) => {
                setStartDate(newStartDate);
                if (startDateError) setStartDateError(null);
                if (
                  endDate &&
                  newStartDate &&
                  moment(endDate).isBefore(moment(newStartDate))
                ) {
                  setEndDate(newStartDate);
                }
              }}
              placeholder={moment(new Date()).format('DD.MM.YYYY')}
              rightSection={<CalendarSvg style={{ height: '16px', width: '16px' }} />}
              rightSectionPointerEvents='none'
              styles={{
                input: {
                  height: '48px',
                  fontSize: '16px',
                  border: startDateError ? '1px solid red' : 'none',
                  color: '#121922',
                  padding: '0 16px',
                },
                section: {
                  marginRight: '6px',
                },
              }}
              highlightToday
              valueFormat={'DD.MM.YYYY'}
              minDate={new Date()}
              clearable
              allowDeselect
              popoverProps={{ zIndex: 100001 }}
            />
            {startDateError && <p className={styles.field_error}>{startDateError}</p>}
          </div>
          <div>
            <Label text='Дата окончания' />
            <DateInput
              value={endDate}
              onChange={setEndDate}
              placeholder={moment(new Date()).format('DD.MM.YYYY')}
              rightSection={<CalendarSvg style={{ height: '16px', width: '16px' }} />}
              rightSectionPointerEvents='none'
              styles={{
                input: {
                  height: '48px',
                  fontSize: '16px',
                  border: endDateError ? '1px solid red' : 'none',
                  color: '#121922',
                  padding: '0 16px',
                },
                section: {
                  marginRight: '6px',
                },
              }}
              highlightToday
              minDate={startDate || new Date()}
              valueFormat={'DD.MM.YYYY'}
              clearable
              allowDeselect={false}
              popoverProps={{ zIndex: 100001 }}
            />

            {endDateError && <p className={styles.field_error}>{endDateError}</p>}

            <div className={styles.labels}>
              <div
                className={classNames(styles.labels_label, {
                  [styles.labels_label_active]: endDatePreset === 'Месяц',
                })}
                onClick={() => handleSelectPreset('Месяц')}>
                Месяц
              </div>
              <div
                className={classNames(styles.labels_label, {
                  [styles.labels_label_active]: endDatePreset === '3 мес',
                })}
                onClick={() => handleSelectPreset('3 мес')}>
                3 месяца
              </div>
              <div
                className={classNames(styles.labels_label, {
                  [styles.labels_label_active]: endDatePreset === '6 мес',
                })}
                onClick={() => handleSelectPreset('6 мес')}>
                6 месяцев
              </div>
              <div
                className={classNames(styles.labels_label, {
                  [styles.labels_label_active]: endDatePreset === 'Год',
                })}
                onClick={() => handleSelectPreset('Год')}>
                Год
              </div>
            </div>
          </div>

          {showBannerDate ? (
            <div className={styles.info}>
              <div className={styles.info_top}>
                <div className={styles.info_title}>
                  <IconAlert />
                  <span>Рекомендуемая длительность - до 3 месяцев</span>
                </div>
              </div>
              <div>
                <p>
                  Длительный или бессрочный договор является одним из ключевых признаков
                  трудовых отношений.
                </p>{' '}
              </div>
            </div>
          ) : null}
        </>
      )}

      <div>
        <p className={styles.terms}>
          Нажимая кнопку «Подписать», ты подписываешь договор с исполнителем и отправляешь
          его на подпись исполнителю в приложение
        </p>
      </div>
      <div>
        <Button
          className={styles.btn}
          onClick={() => {
            handleSubmit();
            sendGoal('InviteContract');
          }}
          disabled={isCreating || isFetching}
          theme='filed'>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {'Подписать'}
            {isCreating ? <Loader size='xs' color='white' /> : <IconTouchId />}
          </div>
        </Button>
      </div>
      {error && (
        <div>
          <p className={styles.error}>{error}</p>
        </div>
      )}
    </div>
  );
};
